import {
  httpGet,
  httpPatch,
  httpPost,
  httpPostFormData,
  httpPut,
  httpDelete,
} from "@/lib/http";
import type { InspectionComparison } from "@/features/submissions/types/inspection.types";
import type { ReprocessQueued } from "@/features/submissions/types/submissions.types";
import { API_ENDPOINTS } from "@/lib/constants/api-endpoints";
import { EXCLUDED_LIMIT } from "../constants/general-liability-options";
import type { PaginatedResponse } from "@/lib/types/http.types";
import type {
  Submission,
  MarketFinderApplicationRead,
  SubmissionUpdatePayload,
  GetSubmissionsParams,
  SubmissionsResponse,
  ScheduleOfHazard,
  CoverLetter,
  CoverLetterRevision,
  CommunicationReviewPage,
  ReviewGroup,
  ReviewGroupResolveRequest,
  AgentOriginThreadHydrationResult,
  CommunicationsSummary,
  ConversationThread,
  ConversationThreadMessage,
  ConversationUnreadState,
  ConversationMessageEmail,
  ConversationReplyAttachmentRef,
  EmailDocumentRead,
  MarketFinderUserPreferences,
  MarketFinderUserPreferencesUpdate,
  AimCompanyPage,
  AimContactImportInput,
  AimContactImportResult,
  AimContactPage,
  AimMarketImportInput,
  MarketFinderActorInfo,
  MarketContact,
  MarketContactDirectoryParams,
  MarketContactDirectoryResponse,
  MarketContactInput,
  MarketCreateInput,
  MarketCreateResponse,
  MarketDirectoryItem,
  MarketsDirectoryParams,
  MarketsDirectoryResponse,
  MarketUpdateInput,
  MarketPacket,
  MarketSuggestionResult,
  MarketPacketPreview,
  MarketPacketSendResult,
  MarketPacketUpdateInput,
  AgentEmailInput,
  MarketEmailInput,
  CommunicationMailbox,
  ConversationReplyDefaults,
  ConversationReplyRecipients,
  MarketOutboundEmailEdit,
  MarketOutboundEmailRead,
  EmailRecipients,
  MailTip,
  PremiseData,
  BuildingData,
  BuildingPayload,
  ApplicationLossRun,
  ApplicationLossPeriod,
  ApplicationLossPeriodUpdate,
  ApplicationLossClaim,
  ApplicationLossClaimUpdate,
  CoverageMetadata,
  ApplicationCoverageData,
  CoverageDataUpdate,
} from "../types/submissions.types";
import type { AuthHeaders } from "@/lib/auth/types";
import type {
  ActionDraftPatch,
  AccountAnswer,
  AccountAnswerCorrection,
  AccountAnswerCreate,
  ConversationInterpretation,
  GroupDraft,
  MarketQuestion,
  MessageFact,
  MessageFactCorrection,
  ProposedAction,
  ProposedActionsResponse,
  Relay,
  RelayInput,
} from "../types/interpretation.types";
import type { HitRateResponse } from "../types/hit-rates.types";
import type {
  ClassCodeInput,
  GLCoverageInput,
} from "../validators/general-liability.validator";

export interface SubmissionEndpointConfig {
  list: string;
  detail: (id: number) => string;
  archive: (id: number) => string;
  underwriters?: string;
  generalLiability: (id: number) => string;
  premises: {
    list: (id: number) => string;
    detail: (id: number, premiseId: number) => string;
  };
  buildings: {
    list: (id: number, premiseId: number) => string;
    detail: (id: number, buildingId: number) => string;
  };
}

const EMAIL_SEND_TIMEOUT = 300000;

// The excludable GL limits carry either an amount or the ``EXCLUDED_LIMIT``
// sentinel, so they cannot go through ``parseInt``: it turns "Excluded" into NaN,
// which ``JSON.stringify`` silently serialises as null — the exclusion would reach
// the backend as "no value" and come back as a blank field. Anything unparseable
// (an empty select) is sent as an explicit null instead of NaN.
function excludableLimit(value: string): number | string | null {
  if (value === EXCLUDED_LIMIT) return EXCLUDED_LIMIT;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

const DEFAULT_ENDPOINTS: SubmissionEndpointConfig = {
  list: API_ENDPOINTS.submissions.list,
  detail: API_ENDPOINTS.submissions.detail,
  archive: API_ENDPOINTS.submissions.archive,
  underwriters: API_ENDPOINTS.submissions.underwriters,
  generalLiability: API_ENDPOINTS.generalLiability.coverage,
  premises: API_ENDPOINTS.premises,
  buildings: API_ENDPOINTS.buildings,
};

export class SubmissionsRepository {
  private endpoints: SubmissionEndpointConfig;

  constructor(endpoints?: SubmissionEndpointConfig) {
    this.endpoints = endpoints || DEFAULT_ENDPOINTS;
  }

  async getSubmissions(
    params: GetSubmissionsParams,
  ): Promise<SubmissionsResponse> {
    const queryParams: Record<string, number | string> = {
      page: params.page,
      size: params.per_page,
    };

    if (params.show_archived !== undefined) {
      queryParams.show_archived = String(params.show_archived);
    }

    if (params.sort?.column && params.sort?.direction) {
      queryParams.sort_column = params.sort.column;
      queryParams.sort_direction = params.sort.direction;
    }

    if (params.filters && params.filters.length > 0) {
      queryParams.filters = JSON.stringify(params.filters);
    }

    if (params.search) {
      queryParams.search = params.search;
    }

    if (params.office_ids && params.office_ids.length > 0) {
      queryParams.office_ids = params.office_ids.join(",");
    }

    if (params.company_ids && params.company_ids.length > 0) {
      queryParams.company_ids = params.company_ids.join(",");
    }

    return httpGet<PaginatedResponse<Submission>>(this.endpoints.list, {
      params: queryParams,
      headers: params.headers,
    });
  }

  async getDistinctUnderwriters(params: {
    office_ids?: number[];
    company_ids?: number[];
    show_archived?: boolean;
    headers: AuthHeaders;
  }): Promise<{ id: string; displayName: string; self?: boolean }[]> {
    const endpoint = this.endpoints.underwriters;
    if (!endpoint) return [];
    const queryParams: Record<string, string> = {};
    if (params.office_ids?.length) {
      queryParams.office_ids = params.office_ids.join(",");
    }
    if (params.company_ids?.length) {
      queryParams.company_ids = params.company_ids.join(",");
    }
    if (params.show_archived) {
      queryParams.show_archived = "true";
    }
    return httpGet<{ id: string; displayName: string; self?: boolean }[]>(endpoint, {
      params: queryParams,
      headers: params.headers,
    });
  }

  async batchAssign(
    applicationIds: number[],
    companyId: number,
    officeId: number | null,
    headers: AuthHeaders,
  ): Promise<{ updated_count: number }> {
    return httpPatch<{ updated_count: number }>(
      API_ENDPOINTS.submissions.batchAssign,
      {
        application_ids: applicationIds,
        company_id: companyId,
        office_id: officeId,
      },
      { headers },
    );
  }

  async getSubmissionById(
    id: number,
    headers: AuthHeaders,
  ): Promise<Submission> {
    return httpGet<Submission>(this.endpoints.detail(id), { headers });
  }

  /** The inspection report compared against the submitted documents (MR-473).
   *
   * 404s when the submission carries an inspection Record but no comparison — the tab is
   * shown off the Record, which exists from classification, while the comparison is
   * written later in ingestion. Callers must treat that as "not compared yet", not as an
   * error and not as an empty comparison. */
  async getInspection(id: number): Promise<InspectionComparison> {
    return httpGet<InspectionComparison>(API_ENDPOINTS.submissions.inspection(id));
  }

  async reprocess(id: number, headers: AuthHeaders): Promise<ReprocessQueued> {
    return httpPost<ReprocessQueued>(API_ENDPOINTS.submissions.reprocess(id), {}, { headers });
  }

  /** Market Finder's own detail read: same application data, plus the
   * resolved agent address the endpoint alone carries. */
  async getMarketFinderApplicationById(
    id: number,
    headers: AuthHeaders,
  ): Promise<MarketFinderApplicationRead> {
    return httpGet<MarketFinderApplicationRead>(
      API_ENDPOINTS.marketFinder.detail(id),
      { headers },
    );
  }

  async archiveSubmission(
    id: number,
    archive: boolean,
    headers: AuthHeaders,
  ): Promise<void> {
    await httpPatch(
      this.endpoints.archive(id),
      {
        is_active: !archive,
      },
      { headers },
    );
  }

  async unarchiveSubmission(
    id: number,
    headers: AuthHeaders,
  ): Promise<void> {
    await this.archiveSubmission(id, false, headers);
  }

  async addClassCode(
    submissionId: number,
    data: ClassCodeInput,
    headers: AuthHeaders,
  ): Promise<ScheduleOfHazard> {
    const payload = {
      class_code: data.class_code,
      classification: data.description,
      exposure: data.if_any ? 0 : data.payroll || 0,
      premium_basis: "Payroll",
      exposure_unit: "per $1000",
      if_any: data.if_any ?? false,
    };

    return httpPost<ScheduleOfHazard>(
      API_ENDPOINTS.generalLiability.scheduleOfHazard(submissionId),
      payload,
      { headers },
    );
  }

  async updateGeneralLiabilitySchedule(
    submissionId: number,
    scheduleOfHazard: ScheduleOfHazard[],
    headers: AuthHeaders,
  ): Promise<{ schedule_of_hazard: ScheduleOfHazard[] }> {
    return httpPatch<{ schedule_of_hazard: ScheduleOfHazard[] }>(
      this.endpoints.generalLiability(submissionId),
      { schedule_of_hazard: scheduleOfHazard },
      { headers },
    );
  }

  async updateClassCode(
    submissionId: number,
    classCodeId: number,
    data: ClassCodeInput,
    allCodes: ScheduleOfHazard[],
    headers: AuthHeaders,
  ): Promise<{ schedule_of_hazard: ScheduleOfHazard[] }> {
    const updatedCodes = allCodes.map((code) =>
      code.id === classCodeId
        ? {
            ...code,
            class_code: data.class_code,
            classification: data.description,
            exposure: data.if_any ? 0 : data.payroll || 0,
            premium_basis: data.premium_basis || "P",
            if_any: data.if_any ?? code.if_any ?? false,
            inferred_fields: [],
          }
        : code,
    );

    // PATCH with complete array (multi-rater pattern)
    return httpPatch<{ schedule_of_hazard: ScheduleOfHazard[] }>(
      this.endpoints.generalLiability(submissionId),
      { schedule_of_hazard: updatedCodes },
      { headers },
    );
  }

  async deleteClassCode(
    submissionId: number,
    remainingCodes: ScheduleOfHazard[],
    headers: AuthHeaders,
  ): Promise<{ schedule_of_hazard: ScheduleOfHazard[] }> {
    return httpPatch<{ schedule_of_hazard: ScheduleOfHazard[] }>(
      this.endpoints.generalLiability(submissionId),
      { schedule_of_hazard: remainingCodes },
      { headers },
    );
  }

  async updateSubmission(
    submissionId: number,
    data: SubmissionUpdatePayload,
    headers: AuthHeaders,
  ): Promise<Submission> {
    return httpPatch<Submission>(this.endpoints.detail(submissionId), data, {
      headers,
    });
  }

  async updateGLPartial(
    submissionId: number,
    data: Record<string, unknown>,
    headers: AuthHeaders,
  ): Promise<void> {
    await httpPatch(this.endpoints.generalLiability(submissionId), data, {
      headers,
    });
  }

  async updateGLCoverage(
    submissionId: number,
    data: GLCoverageInput & { inferred_fields?: unknown[] },
    headers: AuthHeaders,
  ): Promise<void> {
    await httpPatch(
      this.endpoints.generalLiability(submissionId),
      {
        each_occurrence: parseInt(data.each_occurrence, 10),
        general_aggregate_limit: parseInt(data.general_aggregate_limit, 10),
        products_completed_operations_aggregate: excludableLimit(
          data.products_completed_operations_aggregate,
        ),
        personal_advertising_injury: excludableLimit(
          data.personal_advertising_injury,
        ),
        damage_to_rented_premises: excludableLimit(
          data.damage_to_rented_premises,
        ),
        medical_expense: excludableLimit(data.medical_expense),
        employee_benefits: data.employee_benefits,
        deductible_type: data.deductible_type ?? "",
        deductible_amount: parseInt(data.deductible_amount, 10),
        inferred_fields: data.inferred_fields ?? [],
      },
      { headers },
    );
  }

  async getAllClassCodes(): Promise<ClassCodeSearchResult[]> {
    const response = await httpGet<
      Record<string, { description: string; premium_unit?: string | null }>
    >(API_ENDPOINTS.classCodes.all);

    return Object.entries(response).map(([code, data]) => ({
      ClassCode: code,
      ClassDescription: data.description,
      PremiumUnit: data.premium_unit ?? null,
      EGuideLink: "",
      score: 1,
      match_type: "exact",
    }));
  }

  async getAllPropertyClassCodes(): Promise<
    Array<{ code: string; description: string }>
  > {
    const response = await httpGet<
      Record<string, { description: string }>
    >(API_ENDPOINTS.classCodes.property);

    return Object.entries(response).map(([code, data]) => ({
      code,
      description: data.description,
    }));
  }

  async searchClassCodes(
    query: string,
    limit: number = 50,
  ): Promise<ClassCodeSearchResult[]> {
    const params = { query, limit: String(limit) };
    return httpGet<ClassCodeSearchResult[]>(
      API_ENDPOINTS.submissions.classCodesSearch,
      {
        params,
      },
    );
  }

  async createPremise(
    submissionId: number,
    data: { full_address: string; description_of_operations?: string },
    headers: AuthHeaders,
  ): Promise<PremiseData> {
    return httpPost<PremiseData>(
      this.endpoints.premises.list(submissionId),
      data,
      { headers },
    );
  }

  async updatePremise(
    submissionId: number,
    premiseId: number,
    data: {
      full_address?: string;
      description_of_operations?: string;
      nature_of_business?: string;
      location_number?: number;
    },
    headers: AuthHeaders,
  ): Promise<PremiseData> {
    return httpPatch<PremiseData>(
      this.endpoints.premises.detail(submissionId, premiseId),
      data,
      { headers },
    );
  }

  async deletePremise(
    submissionId: number,
    premiseId: number,
    headers: AuthHeaders,
  ): Promise<void> {
    await httpDelete(
      this.endpoints.premises.detail(submissionId, premiseId),
      { headers },
    );
  }

  async createBuilding(
    submissionId: number,
    premiseId: number,
    data: BuildingPayload,
    headers: AuthHeaders,
  ): Promise<BuildingData> {
    return httpPost<BuildingData>(
      this.endpoints.buildings.list(submissionId, premiseId),
      data,
      { headers },
    );
  }

  async updateBuilding(
    submissionId: number,
    buildingId: number,
    data: BuildingPayload,
    headers: AuthHeaders,
  ): Promise<BuildingData> {
    return httpPatch<BuildingData>(
      this.endpoints.buildings.detail(submissionId, buildingId),
      data,
      { headers },
    );
  }

  async deleteBuilding(
    submissionId: number,
    buildingId: number,
    headers: AuthHeaders,
  ): Promise<void> {
    await httpDelete(
      this.endpoints.buildings.detail(submissionId, buildingId),
      { headers },
    );
  }

  // Cover letter methods

  async generateCoverLetter(
    appId: number,
    data: {
      carrier_id?: string;
      carrier_name?: string;
      recipient_email?: string;
    },
  ): Promise<CoverLetter> {
    return httpPost<CoverLetter>(
      API_ENDPOINTS.marketFinder.coverLetterGenerate(appId),
      data,
    );
  }

  async getCoverLetters(appId: number): Promise<CoverLetter[]> {
    return httpGet<CoverLetter[]>(API_ENDPOINTS.marketFinder.coverLetters(appId));
  }

  async getCoverLetter(appId: number, clId: number): Promise<CoverLetter> {
    return httpGet<CoverLetter>(
      API_ENDPOINTS.marketFinder.coverLetterDetail(appId, clId),
    );
  }

  async updateCoverLetter(
    appId: number,
    clId: number,
    data: {
      subject?: string;
      body_html?: string;
      recipient_email?: string;
      revision_source?: "AI_ASSIST" | "MANUAL";
      revision_id?: number;
      action?: "improve" | "formal" | "concise" | "expand";
    },
  ): Promise<CoverLetter> {
    return httpPatch<CoverLetter>(
      API_ENDPOINTS.marketFinder.coverLetterDetail(appId, clId),
      data,
    );
  }

  async aiAssist(data: {
    text: string;
    action: "improve" | "formal" | "concise" | "expand";
    app_id?: number;
    cover_letter_id?: number;
  }): Promise<{ text: string }> {
    return httpPost<{ text: string }>(API_ENDPOINTS.marketFinder.aiAssist, data);
  }

  async getMarketFinderUserPreferences(): Promise<MarketFinderUserPreferences> {
    return httpGet<MarketFinderUserPreferences>(
      API_ENDPOINTS.marketFinder.userPreferences,
    );
  }

  async updateMarketFinderUserPreferences(
    data: MarketFinderUserPreferencesUpdate,
  ): Promise<MarketFinderUserPreferences> {
    return httpPatch<MarketFinderUserPreferences>(
      API_ENDPOINTS.marketFinder.userPreferences,
      data,
    );
  }

  async getHitRates(classCodes: string[]): Promise<HitRateResponse> {
    return httpGet<HitRateResponse>(
      API_ENDPOINTS.marketFinder.hitRates(classCodes),
    );
  }

  async getMarketSuggestions(appId: number): Promise<MarketSuggestionResult> {
    return httpGet<MarketSuggestionResult>(
      API_ENDPOINTS.marketFinder.marketSuggestions(appId),
    );
  }

  async refreshMarketSuggestions(appId: number): Promise<MarketSuggestionResult> {
    return httpPost<MarketSuggestionResult>(
      API_ENDPOINTS.marketFinder.refreshMarketSuggestions(appId),
      {},
    );
  }

  async getPackets(appId: number): Promise<MarketPacket[]> {
    return httpGet<MarketPacket[]>(API_ENDPOINTS.marketFinder.packets(appId));
  }

  async getLossRun(appId: number): Promise<ApplicationLossRun> {
    return httpGet<ApplicationLossRun>(API_ENDPOINTS.marketFinder.lossRun(appId));
  }

  async getCoverageCatalog(): Promise<CoverageMetadata[]> {
    return httpGet<CoverageMetadata[]>(API_ENDPOINTS.marketFinder.coverageCatalog);
  }

  async replaceRequestedCoverages(
    appId: number,
    coverages: string[],
    confirmPacketChanges = false,
    headers?: AuthHeaders,
  ): Promise<Submission> {
    return httpPut<Submission>(
      API_ENDPOINTS.marketFinder.requestedCoverages(appId),
      { coverages, confirm_packet_changes: confirmPacketChanges },
      headers ? { headers } : undefined,
    );
  }

  async getCoverageData(appId: number): Promise<ApplicationCoverageData> {
    return httpGet<ApplicationCoverageData>(
      API_ENDPOINTS.marketFinder.coverageData(appId),
    );
  }

  async createCoverageData(
    appId: number,
    family: string,
    data: CoverageDataUpdate & { coverage_code?: string; premise_id?: number },
  ): Promise<ApplicationCoverageData> {
    return httpPost<ApplicationCoverageData>(
      API_ENDPOINTS.marketFinder.coverageDataFamily(appId, family),
      data,
    );
  }

  async updateCoverageData(
    appId: number,
    family: string,
    rowId: number,
    data: CoverageDataUpdate,
  ): Promise<ApplicationCoverageData> {
    return httpPatch<ApplicationCoverageData>(
      API_ENDPOINTS.marketFinder.coverageDataFamily(appId, family, rowId),
      data,
    );
  }

  async updateCoverageDataItem(
    appId: number,
    itemType: string,
    rowId: number,
    data: CoverageDataUpdate,
  ): Promise<ApplicationCoverageData> {
    return httpPatch<ApplicationCoverageData>(
      API_ENDPOINTS.marketFinder.coverageDataItem(appId, itemType, rowId),
      data,
    );
  }

  async createCoverageDataItem(
    appId: number,
    itemType: string,
    parentId: number,
    data: CoverageDataUpdate = {},
  ): Promise<ApplicationCoverageData> {
    return httpPost<ApplicationCoverageData>(
      API_ENDPOINTS.marketFinder.coverageDataItem(appId, itemType, parentId),
      data,
    );
  }

  async updateLossPeriod(
    appId: number,
    periodId: number,
    data: ApplicationLossPeriodUpdate,
  ): Promise<ApplicationLossPeriod> {
    return httpPatch<ApplicationLossPeriod>(
      API_ENDPOINTS.marketFinder.lossPeriod(appId, periodId),
      data,
    );
  }

  async updateLossClaim(
    appId: number,
    claimId: number,
    data: ApplicationLossClaimUpdate,
  ): Promise<ApplicationLossClaim> {
    return httpPatch<ApplicationLossClaim>(
      API_ENDPOINTS.marketFinder.lossClaim(appId, claimId),
      data,
    );
  }

  async createPacket(
    appId: number,
    data: { label?: string; coverages: string[] },
  ): Promise<MarketPacket> {
    return httpPost<MarketPacket>(API_ENDPOINTS.marketFinder.packets(appId), data);
  }

  async updatePacket(
    appId: number,
    packetId: number,
    data: MarketPacketUpdateInput,
  ): Promise<MarketPacket> {
    return httpPatch<MarketPacket>(
      API_ENDPOINTS.marketFinder.packetDetail(appId, packetId),
      data,
    );
  }

  async deletePacket(appId: number, packetId: number): Promise<void> {
    await httpDelete(API_ENDPOINTS.marketFinder.packetDetail(appId, packetId));
  }

  async setPacketAttachments(
    appId: number,
    packetId: number,
    recordIds: number[],
  ): Promise<MarketPacket> {
    return httpPut<MarketPacket>(
      API_ENDPOINTS.marketFinder.packetAttachments(appId, packetId),
      { record_ids: recordIds },
    );
  }

  async setPacketRecipients(
    appId: number,
    packetId: number,
    recipients: Array<{ carrier_id: string; contact_id: number }>,
  ): Promise<MarketPacket> {
    return httpPut<MarketPacket>(
      API_ENDPOINTS.marketFinder.packetRecipients(appId, packetId),
      { recipients },
    );
  }

  async generatePacketCoverLetter(
    appId: number,
    packetId: number,
    data: {
      current_body_html?: string;
      format?: "standard" | "concise" | "detailed";
    } = {},
  ): Promise<CoverLetter> {
    return httpPost<CoverLetter>(
      API_ENDPOINTS.marketFinder.packetCoverLetterGenerate(appId, packetId),
      data,
    );
  }

  async updatePacketCoverLetter(
    appId: number,
    packetId: number,
    data: {
      subject?: string;
      body_html?: string;
      revision_source?: "AI_ASSIST" | "MANUAL";
      revision_id?: number;
      action?: string;
    },
  ): Promise<CoverLetter> {
    return httpPatch<CoverLetter>(
      API_ENDPOINTS.marketFinder.packetCoverLetter(appId, packetId),
      data,
    );
  }

  async createNewPacketCoverLetter(
    appId: number,
    packetId: number,
  ): Promise<CoverLetter> {
    return httpPost<CoverLetter>(
      API_ENDPOINTS.marketFinder.packetCoverLetterNew(appId, packetId),
      {},
    );
  }

  async getPacketCoverLetterRevisions(
    appId: number,
    packetId: number,
  ): Promise<CoverLetterRevision[]> {
    return httpGet<CoverLetterRevision[]>(
      API_ENDPOINTS.marketFinder.packetCoverLetterRevisions(appId, packetId),
    );
  }

  async previewPacket(appId: number, packetId: number): Promise<MarketPacketPreview> {
    return httpPost<MarketPacketPreview>(
      API_ENDPOINTS.marketFinder.packetPreview(appId, packetId),
      {},
    );
  }

  async sendPacket(appId: number, packetId: number): Promise<MarketPacketSendResult> {
    return httpPost<MarketPacketSendResult>(
      API_ENDPOINTS.marketFinder.packetSend(appId, packetId),
      {},
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async sendAgentEmail(
    appId: number,
    data: AgentEmailInput,
  ): Promise<MarketOutboundEmailRead> {
    return httpPost<MarketOutboundEmailRead>(
      API_ENDPOINTS.marketFinder.agentConversations(appId),
      data,
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async listCommunicationMailboxes(appId: number): Promise<CommunicationMailbox[]> {
    return httpGet<CommunicationMailbox[]>(
      API_ENDPOINTS.marketFinder.communicationMailboxes(appId),
    );
  }

  async sendMarketEmail(
    appId: number,
    conversationId: number,
    data: MarketEmailInput,
  ): Promise<MarketOutboundEmailRead> {
    return httpPost<MarketOutboundEmailRead>(
      API_ENDPOINTS.marketFinder.conversationMessages(appId, conversationId),
      data,
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async listOutboundEmails(
    appId: number,
    conversationId?: number,
  ): Promise<MarketOutboundEmailRead[]> {
    return httpGet<MarketOutboundEmailRead[]>(
      API_ENDPOINTS.marketFinder.outboundEmails(appId, conversationId),
    );
  }

  async retryOutboundEmail(
    appId: number,
    outboundEmailId: number,
  ): Promise<MarketOutboundEmailRead> {
    return httpPost<MarketOutboundEmailRead>(
      API_ENDPOINTS.marketFinder.outboundEmailRetry(appId, outboundEmailId),
      {},
    );
  }

  async editOutboundEmail(
    appId: number,
    outboundEmailId: number,
    data: MarketOutboundEmailEdit,
  ): Promise<MarketOutboundEmailRead> {
    return httpPatch<MarketOutboundEmailRead>(
      API_ENDPOINTS.marketFinder.outboundEmail(appId, outboundEmailId),
      data,
    );
  }

  async uploadAgentEmailAttachments(
    appId: number,
    formData: FormData,
  ): Promise<ConversationReplyAttachmentRef[]> {
    return httpPostFormData<ConversationReplyAttachmentRef[]>(
      API_ENDPOINTS.marketFinder.agentConversationAttachments(appId),
      formData,
    );
  }

  async getCommunicationsSummary(
    underwriterIds?: string[],
  ): Promise<CommunicationsSummary> {
    const suffix = underwriterIds?.length
      ? `?underwriter_ids=${encodeURIComponent(underwriterIds.join(","))}`
      : "";
    return httpGet<CommunicationsSummary>(
      `${API_ENDPOINTS.marketFinder.communicationsSummary}${suffix}`,
    );
  }

  async listApplicationConversations(
    appId: number,
  ): Promise<ConversationThread[]> {
    return httpGet<ConversationThread[]>(
      API_ENDPOINTS.marketFinder.conversations(appId),
    );
  }

  async listEmailDocuments(appId: number): Promise<EmailDocumentRead[]> {
    return httpGet<EmailDocumentRead[]>(
      API_ENDPOINTS.marketFinder.emailDocuments(appId),
    );
  }

  async hydrateAgentOriginThread(
    appId: number,
  ): Promise<AgentOriginThreadHydrationResult> {
    return httpPost<AgentOriginThreadHydrationResult>(
      API_ENDPOINTS.marketFinder.agentOriginThreadHydrate(appId),
      {},
    );
  }

  async listConversationMessages(
    appId: number,
    conversationId: number,
  ): Promise<ConversationThreadMessage[]> {
    return httpGet<ConversationThreadMessage[]>(
      API_ENDPOINTS.marketFinder.conversationMessages(appId, conversationId),
    );
  }

  async retryConversationMessageInterpretation(
    appId: number,
    messageId: number,
  ): Promise<ConversationThreadMessage> {
    return httpPost<ConversationThreadMessage>(
      API_ENDPOINTS.marketFinder.conversationMessageInterpretationRetry(
        appId,
        messageId,
      ),
      {},
    );
  }

  async getConversationInterpretation(
    appId: number,
    conversationId: number,
  ): Promise<ConversationInterpretation> {
    return httpGet<ConversationInterpretation>(
      API_ENDPOINTS.marketFinder.conversationInterpretation(appId, conversationId),
    );
  }

  async dismissConversationFact(
    appId: number,
    factId: number,
  ): Promise<MessageFact> {
    return httpPost<MessageFact>(
      API_ENDPOINTS.marketFinder.conversationFactDismiss(appId, factId),
      {},
    );
  }

  async correctConversationFact(
    appId: number,
    factId: number,
    correction: MessageFactCorrection,
  ): Promise<MessageFact> {
    return httpPatch<MessageFact>(
      API_ENDPOINTS.marketFinder.conversationFact(appId, factId),
      correction,
    );
  }

  async listQuestions(
    appId: number,
    conversationId?: number,
  ): Promise<MarketQuestion[]> {
    return httpGet<MarketQuestion[]>(
      API_ENDPOINTS.marketFinder.questions(appId, conversationId),
    );
  }

  async listAccountAnswers(appId: number): Promise<AccountAnswer[]> {
    return httpGet<AccountAnswer[]>(API_ENDPOINTS.marketFinder.accountAnswers(appId));
  }

  async createAccountAnswer(
    appId: number,
    payload: AccountAnswerCreate,
  ): Promise<AccountAnswer> {
    return httpPost<AccountAnswer>(
      API_ENDPOINTS.marketFinder.accountAnswers(appId),
      payload,
    );
  }

  async correctAccountAnswer(
    appId: number,
    answerId: number,
    correction: AccountAnswerCorrection,
  ): Promise<AccountAnswer> {
    return httpPatch<AccountAnswer>(
      API_ENDPOINTS.marketFinder.accountAnswer(appId, answerId),
      correction,
    );
  }

  async deleteAccountAnswer(appId: number, answerId: number): Promise<void> {
    await httpDelete(API_ENDPOINTS.marketFinder.accountAnswer(appId, answerId));
  }

  async confirmAccountAnswerLink(appId: number, linkId: number): Promise<MarketQuestion> {
    return httpPost<MarketQuestion>(API_ENDPOINTS.marketFinder.accountAnswerLinkConfirm(appId, linkId), {});
  }

  async dismissAccountAnswerLink(appId: number, linkId: number): Promise<MarketQuestion> {
    return httpPost<MarketQuestion>(API_ENDPOINTS.marketFinder.accountAnswerLinkDismiss(appId, linkId), {});
  }

  async correctQuestionText(
    appId: number,
    questionId: number,
    text: string,
  ): Promise<MarketQuestion> {
    return httpPatch<MarketQuestion>(
      API_ENDPOINTS.marketFinder.question(appId, questionId),
      { text },
    );
  }

  async reopenQuestion(appId: number, questionId: number): Promise<MarketQuestion> {
    return httpPost<MarketQuestion>(
      API_ENDPOINTS.marketFinder.questionReopen(appId, questionId),
      {},
    );
  }

  async closeQuestion(appId: number, questionId: number): Promise<MarketQuestion> {
    return httpPost<MarketQuestion>(
      API_ENDPOINTS.marketFinder.questionClose(appId, questionId),
      {},
    );
  }

  async confirmAnswerLink(
    appId: number,
    linkId: number,
    partial: boolean,
  ): Promise<MarketQuestion> {
    return httpPost<MarketQuestion>(
      API_ENDPOINTS.marketFinder.questionLinkConfirm(appId, linkId),
      { partial },
    );
  }

  async dismissAnswerLink(appId: number, linkId: number): Promise<MarketQuestion> {
    return httpPost<MarketQuestion>(
      API_ENDPOINTS.marketFinder.questionLinkDismiss(appId, linkId),
      {},
    );
  }

  async createRelay(appId: number, relay: RelayInput): Promise<MarketOutboundEmailRead> {
    return httpPost<MarketOutboundEmailRead>(
      API_ENDPOINTS.marketFinder.relays(appId),
      relay,
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async listRelays(appId: number, conversationId?: number): Promise<Relay[]> {
    return httpGet<Relay[]>(
      API_ENDPOINTS.marketFinder.relays(appId, conversationId),
    );
  }

  async listProposedActions(
    appId: number,
    conversationId?: number,
    messageId?: number,
  ): Promise<ProposedActionsResponse> {
    return httpGet<ProposedActionsResponse>(
      API_ENDPOINTS.marketFinder.proposedActions(appId, conversationId, messageId),
    );
  }

  async editActionDraft(
    appId: number,
    draftId: number,
    patch: ActionDraftPatch,
  ): Promise<ProposedAction> {
    return httpPatch<ProposedAction>(
      API_ENDPOINTS.marketFinder.actionDraft(appId, draftId),
      patch,
    );
  }

  async approveProposedAction(
    appId: number,
    actionId: number,
    clientRequestId: string,
    attachments?: ConversationReplyAttachmentRef[],
    recipients?: EmailRecipients,
    targetMessageId?: number,
    sendFrom?: string,
  ): Promise<MarketOutboundEmailRead> {
    // Approving executes the action: a real email goes out through the relay
    // machinery — same long timeout as every other outbound send. An omitted
    // attachment selection keeps the server default (every file the draft
    // offers); an explicit list — possibly empty — is exactly what gets sent.
    // The server resolves the reply parent itself and rejects a supplied one,
    // so `target_message_id` travels only for a `choose_target` send.
    return httpPost<MarketOutboundEmailRead>(
      API_ENDPOINTS.marketFinder.proposedActionApprove(appId, actionId),
      {
        client_request_id: clientRequestId,
        ...(attachments ? { attachments } : {}),
        ...(targetMessageId != null ? { target_message_id: targetMessageId } : {}),
        ...(recipients ?? {}),
        ...(sendFrom ? { send_from: sendFrom } : {}),
      },
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async dismissProposedAction(
    appId: number,
    actionId: number,
  ): Promise<ProposedAction> {
    return httpPost<ProposedAction>(
      API_ENDPOINTS.marketFinder.proposedActionDismiss(appId, actionId),
      {},
    );
  }

  /** Defer a follow-up by `days`. Unlike dismiss this is reversible —
   * the sweep brings the same action back once the interval passes. */
  async snoozeProposedAction(
    appId: number,
    actionId: number,
    days: number,
  ): Promise<ProposedAction> {
    return httpPost<ProposedAction>(
      API_ENDPOINTS.marketFinder.proposedActionSnooze(appId, actionId),
      { days },
    );
  }

  async editGroupDraft(
    appId: number,
    draftId: number,
    patch: ActionDraftPatch,
  ): Promise<GroupDraft> {
    return httpPatch<GroupDraft>(
      API_ENDPOINTS.marketFinder.groupDraft(appId, draftId),
      patch,
    );
  }

  async approveProposedActionsBatch(
    appId: number,
    actionIds: number[],
    clientRequestId: string,
    attachments?: ConversationReplyAttachmentRef[],
    recipients?: EmailRecipients,
    targetMessageId?: number,
    sendFrom?: string,
  ): Promise<MarketOutboundEmailRead> {
    // ONE outbound email for the whole same-target group: same selection,
    // reply-parent and send-timeout semantics as the single approve.
    return httpPost<MarketOutboundEmailRead>(
      API_ENDPOINTS.marketFinder.proposedActionApproveBatch(appId),
      {
        client_request_id: clientRequestId,
        action_ids: actionIds,
        ...(attachments ? { attachments } : {}),
        ...(targetMessageId != null ? { target_message_id: targetMessageId } : {}),
        ...(recipients ?? {}),
        ...(sendFrom ? { send_from: sendFrom } : {}),
      },
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async getConversationMessageEmail(
    appId: number,
    messageId: number,
  ): Promise<ConversationMessageEmail> {
    return httpGet<ConversationMessageEmail>(
      API_ENDPOINTS.marketFinder.conversationMessageEmail(appId, messageId),
    );
  }

  /** Pending review groups whose candidates attribute to this application —
   * the inline possible-match cards on the Communication tab. Same entity as
   * the standalone review queue, scoped to one submission. */
  async listApplicationReviewGroups(appId: number): Promise<ReviewGroup[]> {
    return httpGet<ReviewGroup[]>(
      API_ENDPOINTS.marketFinder.applicationReviewGroups(appId),
    );
  }

  /** Application-scoped reject: this submission's candidates leave the
   * snapshot so the card disappears here; the group remains in the review
   * queue. Distinct from `dismissReviewGroup`. */
  async rejectApplicationReviewGroup(
    appId: number,
    groupId: number,
  ): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.applicationReviewGroupReject(appId, groupId),
      undefined,
    );
  }

  async getEmailLinkBlastRadius(
    appId: number,
    messageId: number,
  ): Promise<{ message_ids: number[] }> {
    return httpGet<{ message_ids: number[] }>(
      API_ENDPOINTS.marketFinder.conversationEmailLinkBlastRadius(appId, messageId),
    );
  }

  async markConversationRead(
    appId: number,
    conversationId: number,
    messageIds: number[],
  ): Promise<ConversationUnreadState> {
    return httpPost<ConversationUnreadState>(
      API_ENDPOINTS.marketFinder.conversationRead(appId, conversationId),
      { message_ids: messageIds },
    );
  }

  async markConversationUnread(
    appId: number,
    conversationId: number,
  ): Promise<ConversationUnreadState> {
    return httpDelete<ConversationUnreadState>(
      API_ENDPOINTS.marketFinder.conversationRead(appId, conversationId),
    );
  }

  async detachEmailFromConversation(
    appId: number,
    messageId: number,
  ): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.conversationEmailDetach(appId, messageId),
      undefined,
    );
  }

  async attachEmailToConversation(
    appId: number,
    messageId: number,
    conversationId: number,
  ): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.conversationEmailAttach(appId, messageId),
      { conversation_id: conversationId },
    );
  }

  async replyToConversation(
    appId: number,
    conversationId: number,
    bodyHtml: string,
    attachments: ConversationReplyAttachmentRef[] = [],
    targetMessageId?: number,
    recipients?: ConversationReplyRecipients,
    sendFrom?: string,
    clientRequestId: string = crypto.randomUUID(),
  ): Promise<MarketOutboundEmailRead> {
    return httpPost<MarketOutboundEmailRead>(
      API_ENDPOINTS.marketFinder.conversationReply(appId, conversationId),
      {
        client_request_id: clientRequestId,
        body_html: bodyHtml,
        attachments,
        ...(targetMessageId == null
          ? {}
          : { target_message_id: targetMessageId }),
        ...(recipients ?? {}),
        ...(sendFrom ? { send_from: sendFrom } : {}),
      },
      { timeout: EMAIL_SEND_TIMEOUT },
    );
  }

  async getConversationReplyDefaults(
    appId: number,
    conversationId: number,
    targetMessageId?: number,
    replyAll = true,
  ): Promise<ConversationReplyDefaults> {
    return httpGet<ConversationReplyDefaults>(
      API_ENDPOINTS.marketFinder.conversationReplyDefaults(
        appId,
        conversationId,
        targetMessageId,
        replyAll,
      ),
    );
  }

  async getEmailMailTips(
    appId: number,
    recipients: string[],
    sendFrom?: string,
  ): Promise<MailTip[]> {
    return httpPost<MailTip[]>(API_ENDPOINTS.marketFinder.emailMailTips(appId), {
      recipients,
      ...(sendFrom ? { send_from: sendFrom } : {}),
    });
  }

  async uploadConversationReplyAttachments(
    appId: number,
    conversationId: number,
    formData: FormData,
  ): Promise<ConversationReplyAttachmentRef[]> {
    return httpPostFormData<ConversationReplyAttachmentRef[]>(
      API_ENDPOINTS.marketFinder.conversationReplyAttachments(appId, conversationId),
      formData,
    );
  }

  /** The standalone Communication Review queue. `filters` is a JSON-encoded
   * `FilterConfig[]` string. */
  async listReviewGroups(options: {
    archived?: boolean;
    page?: number;
    perPage?: number;
    search?: string;
    filters?: string;
  }): Promise<CommunicationReviewPage> {
    const params: Record<string, string> = {
      archived: String(options.archived ?? false),
      page: String(options.page ?? 1),
      per_page: String(options.perPage ?? 25),
    };
    if (options.search) params.search = options.search;
    if (options.filters) params.filters = options.filters;
    return httpGet<CommunicationReviewPage>(
      API_ENDPOINTS.marketFinder.reviewGroups,
      { params },
    );
  }

  async getReviewGroup(groupId: number): Promise<ReviewGroup> {
    return httpGet<ReviewGroup>(API_ENDPOINTS.marketFinder.reviewGroup(groupId));
  }

  /** The backend validates the exact combination of keys in `body` and
   * answers 422 for anything else, so this posts it verbatim. */
  async resolveReviewGroup(
    groupId: number,
    body: ReviewGroupResolveRequest,
  ): Promise<ReviewGroup> {
    return httpPost<ReviewGroup>(
      API_ENDPOINTS.marketFinder.reviewGroupResolve(groupId),
      body,
    );
  }

  async resolveReviewGroupByReference(
    groupId: number,
    conversationReference: string,
  ): Promise<ReviewGroup> {
    return httpPost<ReviewGroup>(
      API_ENDPOINTS.marketFinder.reviewGroupResolve(groupId),
      { conversation_reference: conversationReference },
    );
  }

  async dismissReviewGroup(groupId: number): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.reviewGroupDismiss(groupId),
      undefined,
    );
  }

  async archiveReviewGroup(groupId: number): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.reviewGroupArchive(groupId),
      undefined,
    );
  }

  async unarchiveReviewGroup(groupId: number): Promise<void> {
    return httpPost<void>(
      API_ENDPOINTS.marketFinder.reviewGroupUnarchive(groupId),
      undefined,
    );
  }

  async getReviewGroupMemberEmail(
    messageId: number,
  ): Promise<ConversationMessageEmail> {
    return httpGet<ConversationMessageEmail>(
      API_ENDPOINTS.marketFinder.reviewGroupMemberEmail(messageId),
    );
  }

  async getMarkets(includeInactive?: boolean): Promise<MarketDirectoryItem[]> {
    return httpGet<MarketDirectoryItem[]>(
      API_ENDPOINTS.marketFinder.markets(includeInactive),
    );
  }

  async getMarketsDirectory(
    params: MarketsDirectoryParams,
  ): Promise<MarketsDirectoryResponse> {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page));
    qs.set("size", String(params.size));
    if (params.search) qs.set("search", params.search);
    if (params.show_archived) qs.set("show_archived", "true");
    if (params.market_type) qs.set("market_type", params.market_type);
    if (params.source) qs.set("source", params.source);
    if (params.coverages) qs.set("coverages", params.coverages);
    if (params.sort_column) {
      qs.set("sort_column", params.sort_column);
      qs.set("sort_direction", params.sort_direction ?? "asc");
    }
    return httpGet<MarketsDirectoryResponse>(
      `${API_ENDPOINTS.marketFinder.marketsDirectory}?${qs.toString()}`,
    );
  }

  async setContactActive(
    contactId: number,
    archive: boolean,
  ): Promise<MarketContact> {
    return httpPost<MarketContact>(
      API_ENDPOINTS.marketFinder.marketContactArchive(contactId, archive),
      {},
    );
  }

  async updateMarket(
    carrierId: string,
    data: MarketUpdateInput,
  ): Promise<MarketDirectoryItem> {
    return httpPatch<MarketDirectoryItem>(
      API_ENDPOINTS.marketFinder.marketUpdate(carrierId),
      data,
    );
  }

  async uploadMarketImage(
    carrierId: string,
    file: File,
  ): Promise<MarketDirectoryItem> {
    const formData = new FormData();
    formData.append("file", file);
    return httpPostFormData<MarketDirectoryItem>(
      API_ENDPOINTS.marketFinder.marketImage(carrierId),
      formData,
    );
  }

  async deleteMarketImage(carrierId: string): Promise<MarketDirectoryItem> {
    return httpDelete<MarketDirectoryItem>(
      API_ENDPOINTS.marketFinder.marketImage(carrierId),
    );
  }

  async createMarket(data: MarketCreateInput): Promise<MarketCreateResponse> {
    return httpPost<MarketCreateResponse>(
      API_ENDPOINTS.marketFinder.marketCreate,
      data,
    );
  }

  async getMarketContacts(carrierId: string): Promise<MarketContact[]> {
    return httpGet<MarketContact[]>(
      API_ENDPOINTS.marketFinder.marketContacts(carrierId),
    );
  }

  async getMarketContactsDirectory(
    params: MarketContactDirectoryParams,
  ): Promise<MarketContactDirectoryResponse> {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page));
    qs.set("size", String(params.size));
    if (params.search) qs.set("search", params.search);
    if (params.scope) qs.set("scope", params.scope);
    if (params.source) qs.set("source", params.source);
    if (params.show_archived) qs.set("show_archived", "true");
    if (params.sort_column) {
      qs.set("sort_column", params.sort_column);
      qs.set("sort_direction", params.sort_direction ?? "asc");
    }
    return httpGet<MarketContactDirectoryResponse>(
      `${API_ENDPOINTS.marketFinder.marketContactsDirectory}?${qs.toString()}`,
    );
  }

  async createMarketContact(
    carrierId: string,
    data: MarketContactInput,
  ): Promise<MarketContact> {
    return httpPost<MarketContact>(
      API_ENDPOINTS.marketFinder.marketContacts(carrierId),
      data,
    );
  }

  async updateMarketContact(
    contactId: number,
    data: MarketContactInput,
  ): Promise<MarketContact> {
    return httpPatch<MarketContact>(
      API_ENDPOINTS.marketFinder.marketContact(contactId),
      data,
    );
  }

  async getMarketFinderMe(): Promise<MarketFinderActorInfo> {
    return httpGet<MarketFinderActorInfo>(API_ENDPOINTS.marketFinder.me);
  }

  async searchAimCompanies(
    q: string,
    page = 0,
    size = 20,
  ): Promise<AimCompanyPage> {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    qs.set("page", String(page));
    qs.set("size", String(size));
    return httpGet<AimCompanyPage>(
      `${API_ENDPOINTS.marketFinder.aimCompanies}?${qs.toString()}`,
    );
  }

  async getAimCompanyContacts(
    referenceId: number,
    q = "",
    page = 0,
    size = 20,
    carrierId?: string,
  ): Promise<AimContactPage> {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    qs.set("page", String(page));
    qs.set("size", String(size));
    // Already-imported flags are carrier-specific — same AIM contact can be
    // imported under several markets.
    if (carrierId) qs.set("carrier_id", carrierId);
    return httpGet<AimContactPage>(
      `${API_ENDPOINTS.marketFinder.aimCompanyContacts(referenceId)}?${qs.toString()}`,
    );
  }

  async importAimMarket(data: AimMarketImportInput): Promise<MarketCreateResponse> {
    return httpPost<MarketCreateResponse>(
      API_ENDPOINTS.marketFinder.marketAimImport,
      data,
    );
  }

  async importAimContacts(
    carrierId: string,
    data: AimContactImportInput,
  ): Promise<AimContactImportResult> {
    return httpPost<AimContactImportResult>(
      API_ENDPOINTS.marketFinder.marketContactsAimImport(carrierId),
      data,
    );
  }
}

export interface ClassCodeSearchResult {
  ClassCode: string;
  ClassDescription: string;
  PremiumUnit?: string | null;
  EGuideLink: string;
  score: number;
  match_type: string;
}

export function createSubmissionsRepository(): SubmissionsRepository {
  return new SubmissionsRepository();
}

export function createMarketFinderRepository(): SubmissionsRepository {
  return new SubmissionsRepository({
    list: API_ENDPOINTS.marketFinder.list,
    detail: API_ENDPOINTS.marketFinder.detail,
    archive: API_ENDPOINTS.marketFinder.archive,
    underwriters: API_ENDPOINTS.marketFinder.underwriters,
    generalLiability: API_ENDPOINTS.marketFinder.generalLiability,
    premises: API_ENDPOINTS.marketFinder.premises,
    buildings: API_ENDPOINTS.marketFinder.buildings,
  });
}
